/**
 * Conversation Preview (dev only)
 *
 * Mounts the real AgentAssistantMessage + surface chips in a hard-coded
 * state so we can validate every visual scenario (focus rings, hover
 * overlap, code blocks) without logging in or running a real agent.
 *
 * Visit `/dev/conversation-preview` in dev mode. Extend `SCENARIOS` below
 * to cover new cases.
 */

import { useMemo } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import {
  AssistantRuntimeProvider,
  useLocalRuntime,
  type ChatModelAdapter,
  type ThreadMessageLike,
} from '@assistant-ui/react';
import { Thread } from '@assistant-ui/react-ui';
import '@assistant-ui/react-ui/styles/index.css';
import '@assistant-ui/react-ui/styles/markdown.css';
import { PlatformEventContext } from '@/contexts/PlatformEventContext';
import { CanvasProvider } from '@/contexts/CanvasContext';
import { ThoughtsProvider } from '@/contexts/ThoughtsContext';
import { AgentAssistantMessage } from '@/components/assistant-ui/agent-assistant-message';
import { AgentUserMessage } from '@/components/assistant-ui/agent-user-message';
import { MentionAwareComposer } from '@/components/assistant-ui/mention-aware-composer';
import ToolUI from '@/components/assistant-ui/tool-ui';
import type { Canvas, Surface } from '@/types/canvases';
import type { KnowledgeBase, VfsNode } from '@/types/kb';

// No-op adapter: this preview thread never sends — the composer is hidden
// and reload/retry would throw if hit.
const noopAdapter: ChatModelAdapter = {
  // eslint-disable-next-line require-yield
  run: async function* () {
    throw new Error('ConversationPreviewPage is static — the composer is disabled');
  },
};

// Stub platform event context so CanvasProvider can mount without a WS.
const noopEventContext = {
  subscribe: () => () => {},
  isConnected: false,
  diagnostics: { connectedSince: null, disconnectCount: 0, lastDisconnectReason: null, lastDisconnectTime: null, reconnectAttempts: 0 },
};

const CONV_ID = 'conv-preview';
const RUN_ID = 'run-preview';
const CVS_ID = 'cvs-preview';

const fakeCanvas: Canvas = {
  canvasId: CVS_ID,
  conversationId: CONV_ID,
  title: 'Preview Canvas',
  isDefault: true,
  layout: 'stack',
  createdAt: new Date().toISOString(),
};

const fakeSurfaces: Surface[] = [
  {
    canvasId: CVS_ID,
    surfaceId: 'srf-preview-summary',
    runId: RUN_ID,
    title: 'Summary Agent',
    version: 1,
    spec: { updates: [] },
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    canvasId: CVS_ID,
    surfaceId: 'srf-preview-analyzer',
    runId: RUN_ID,
    title: 'Analyzer Agent',
    version: 1,
    spec: { updates: [] },
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
];

/**
 * Scenarios. Each scenario becomes its own `<ScenarioThread>` with its own
 * `useLocalRuntime` instance — isolates initialMessages reads (the runtime
 * only consumes `initialMessages` on first mount).
 */
const SCENARIOS: { title: string; messages: readonly ThreadMessageLike[] }[] = [
  {
    title: 'Single-turn answer (no tools, no reasoning) — fallback "Thought for Xs" header from runDurationMs',
    messages: [
      {
        role: 'user',
        content: 'What time zone is UTC+1 in summer?',
      },
      {
        role: 'assistant',
        content: [
          {
            type: 'text',
            text: 'In summer, UTC+1 corresponds to **Central European Summer Time (CEST)** is actually UTC+2 — UTC+1 itself is the standard winter offset (CET). So a one-line answer needs no tools or reasoning steps.',
          },
        ],
        // Mirrors a real completed single-turn run: durationMs lands in
        // metadata.custom.runDurationMs even with zero tool calls / steps.
        metadata: { custom: { runId: 'run-single-turn', runDurationMs: 3448 } },
      },
    ],
  },
  {
    title: 'Assistant reply with 2 surface chips + fenced code block (non-last message — triggers floating action bar)',
    messages: [
      {
        role: 'user',
        content: 'Create a summary agent and an analyzer agent, then show me how to invoke them.',
      },
      {
        role: 'assistant',
        content: [
          {
            type: 'text',
            text:
              'The next scheduled run will create the dashboard with the tracking you asked for. ' +
              "After a few runs, you'll see it already covered. Want me to trigger a test end-to-end?\n\n" +
              '```python\nprint("Hello, world!")\n```',
          },
        ],
        metadata: { custom: { runId: RUN_ID } },
      },
      // Follow-up exchange so the assistant message above is "not-last" —
      // which is the condition that triggers the library's autohide +
      // autohideFloat behaviour on the first assistant message. Without
      // this the action bar just renders inline and the overlap bug
      // doesn't reproduce.
      {
        role: 'user',
        content: 'Yes please.',
      },
      {
        role: 'assistant',
        content: [
          {
            type: 'text',
            text: 'Kicking off the test now…',
          },
        ],
        metadata: { custom: { runId: 'run-preview-2' } },
      },
    ],
  },
  {
    title: 'Two consecutive assistant messages (verifies inter-message spacing)',
    messages: [
      {
        role: 'user',
        content: 'Run the comparison.',
      },
      {
        role: 'assistant',
        content: [
          {
            type: 'text',
            text:
              'Comparison is done — **7 of 8 improvements confirmed, 1 partially applied**.\n\n' +
              '- **Duration and tool calls reduced** — the agent is leaner\n' +
              '- **Output tokens increased** — likely the richer story summaries with [discussion links](https://news.ycombinator.com)\n' +
              '- **1 improvement partially applied** — details in the [run detail](/runs/run-preview) and the **"HN Agent Run Comparison"** canvas\n\n' +
              'Want me to have the Architect finish applying the remaining partial improvement?',
          },
        ],
        metadata: { custom: { runId: RUN_ID } },
      },
      {
        role: 'assistant',
        content: [
          {
            type: 'text',
            text:
              'Delegated to the **Agent Architect** — it\'s inspecting the run trace with a focus on duration, tool-call efficiency, and anomalies. The analysis will be surfaced as a canvas artifact.',
          },
        ],
        metadata: { custom: { runId: 'run-preview-2' } },
      },
    ],
  },
  {
    title: 'User message with both #surface and @agent mentions (verifies chip rendering for both directive types)',
    messages: [
      {
        role: 'user',
        content:
          'Can you ask the :agent[Jira Expert]{name=agt-01preview-jira} to look at :surface[Quarterly Roadmap]{name=cvs-preview:srf-preview-summary} and report blockers?',
      },
      {
        role: 'assistant',
        content: [
          {
            type: 'text',
            text: 'On it — delegating to Jira Expert with the Quarterly Roadmap context.',
          },
        ],
        metadata: { custom: { runId: 'run-preview-mentions' } },
      },
    ],
  },
  {
    title:
      'Platform-injected turn (source: event_notification) — collapses to a system-event chip, expandable to the full note',
    messages: [
      {
        role: 'user',
        content: 'what tools do you have available?',
      },
      {
        role: 'assistant',
        content: [
          {
            type: 'text',
            text: 'I have three tools, but the Example MCP server needs your authorization first.',
          },
        ],
        metadata: { custom: { runId: 'run-preview-sysnote-1' } },
      },
      {
        role: 'user',
        content:
          'SYSTEM NOTE: The user just completed authorization for "Example MCP".\nThe credential that blocked your earlier tool call is now available.\n\nRetry the action you were blocked on. Do not ask the user to authorize again and do not call connect_mcp_server for this integration.',
        metadata: { custom: { source: 'event_notification' } },
      },
      {
        role: 'assistant',
        content: [
          {
            type: 'text',
            text: 'Both tools work now — Generate Id: `customer-2xckklsr8klq`, Slugify: `example-tools-assistant-demo`.',
          },
        ],
        metadata: { custom: { runId: 'run-preview-sysnote-2' } },
      },
    ],
  },
  {
    title: 'MCP tool returned requiresAuth — inline Connect callout (verifies ToolCallCard auth-shape detection)',
    messages: [
      {
        role: 'user',
        content: 'List my Linear issues.',
      },
      {
        role: 'assistant',
        content: [
          {
            type: 'tool-call',
            toolCallId: 'tc-preview-mcp-auth',
            toolName: 'linear_list_issues',
            args: { limit: 10 },
            result: {
              requiresAuth: true,
              serverId: 'mcp-linear-preview',
              serverName: 'Linear MCP',
              authorizationUrl: 'https://example.com/oauth/authorize?state=preview',
              hint: 'Linear needs you to authorize before the agent can read your issues.',
            },
          },
          {
            type: 'text',
            text: 'I need you to connect Linear before I can read your issues. Once you authorize, re-send this message.',
          },
        ],
        metadata: { custom: { runId: 'run-preview-mcp' } },
      },
    ],
  },
];

function ScenarioThread({ messages }: { messages: readonly ThreadMessageLike[] }) {
  const runtime = useLocalRuntime(noopAdapter, { initialMessages: messages });
  return (
    <AssistantRuntimeProvider runtime={runtime}>
      <ToolUI />
      <Thread.Root>
        <Thread.Messages components={{ UserMessage: AgentUserMessage, AssistantMessage: AgentAssistantMessage }} />
      </Thread.Root>
    </AssistantRuntimeProvider>
  );
}

// Mock KBs + their flat file lists, seeded into a local react-query cache so
// the `!` picker renders merged source-tagged rows without a backend. Keys
// match the production hooks: `['me','kb','list']` (useMeKnowledgeBases) and
// `['kb', kbId, 'entries-flat']` (walkKbTree, shared with the files-backend).
const mockKbs: KnowledgeBase[] = [
  { kbId: 'kbs-sales01', orgId: 'org-x', scope: 'space', name: 'Sales KB', status: 'active', createdAt: '', updatedAt: '' },
  { kbId: 'kbs-eng0001', orgId: 'org-x', scope: 'org', name: 'Engineering', status: 'active', createdAt: '', updatedAt: '' },
];
function fileNode(kbId: string, path: string): VfsNode {
  return { kbId, path, name: path.split('/').pop() as string, kind: 'file', createdAt: '', updatedAt: '' };
}
const mockKbEntries: Record<string, VfsNode[]> = {
  'kbs-sales01': [fileNode('kbs-sales01', 'reports/q4-report.pdf'), fileNode('kbs-sales01', 'pricing.xlsx')],
  'kbs-eng0001': [fileNode('kbs-eng0001', 'specs/api-spec.docx'), fileNode('kbs-eng0001', 'runbook.md')],
};

/**
 * Mounts MentionAwareComposer in isolation so we can validate the chip strip
 * and the mention popovers without auth or a real backend. A nested,
 * pre-seeded QueryClient supplies mock KBs + files so the `!` picker shows
 * merged Conversation/KB rows.
 */
function MentionComposerPreview() {
  const runtime = useLocalRuntime(noopAdapter, { initialMessages: [] });
  const queryClient = useMemo(() => {
    const qc = new QueryClient({
      defaultOptions: { queries: { retry: false, staleTime: Infinity, gcTime: Infinity } },
    });
    qc.setQueryData(['me', 'kb', 'list'], { items: mockKbs });
    for (const kb of mockKbs) {
      qc.setQueryData(['kb', kb.kbId, 'entries-flat'], { items: mockKbEntries[kb.kbId] ?? [] });
    }
    return qc;
  }, []);
  return (
    <QueryClientProvider client={queryClient}>
      <AssistantRuntimeProvider runtime={runtime}>
        <MentionAwareComposer />
      </AssistantRuntimeProvider>
    </QueryClientProvider>
  );
}

export default function ConversationPreviewPage() {
  const eventCtx = useMemo(() => noopEventContext, []);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <div className="mx-auto max-w-4xl px-6 py-8">
        <header className="mb-6 border-b border-border pb-4">
          <h1 className="text-2xl font-bold">Conversation Preview</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Hard-coded conversation state. Used for visual validation of{' '}
            <code className="rounded bg-muted px-1 py-0.5">AgentAssistantMessage</code>,
            surface chips, action bar, and related conversation UI. Dev only.
          </p>
        </header>

        <PlatformEventContext.Provider value={eventCtx}>
          <CanvasProvider
            conversationId={CONV_ID}
            initialCanvas={{ canvas: fakeCanvas, surfaces: fakeSurfaces }}
          >
            <ThoughtsProvider>
              {SCENARIOS.map((s) => (
                <section key={s.title} className="mb-10">
                  <h2 className="mb-3 text-sm font-semibold text-muted-foreground">
                    {s.title}
                  </h2>
                  <div className="rounded-lg border border-border p-4">
                    <ScenarioThread messages={s.messages} />
                  </div>
                </section>
              ))}

              <section className="mb-10">
                <h2 className="mb-3 text-sm font-semibold text-muted-foreground">
                  Mention-aware composer (type <code className="rounded bg-muted px-1 py-0.5">@</code> to open the surface picker)
                </h2>
                <div className="rounded-lg border border-border p-4">
                  <MentionComposerPreview />
                </div>
              </section>
            </ThoughtsProvider>
          </CanvasProvider>
        </PlatformEventContext.Provider>
      </div>
    </div>
  );
}
